/**
 * AtlasPassportEditorModule — customize the portable avatar + display name.
 *
 * A right-side panel (toggled with P) that edits the standardized avatar
 * (body template + material-slot tints), shows a live Avatar Complexity preview,
 * and saves via `setAvatar` (the server recomputes the authoritative score).
 * It also exposes the weekly-rate-limited display-name change. A live preview
 * body is spawned in the main scene in front of the camera while the panel is
 * open, rebuilt from the SAME AvatarFactory remotes use — so what you author is
 * exactly what other players will see.
 */

import type { App, IModule } from 'enginex';
import type * as THREE from 'three/webgpu';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { buildAvatar, type AvatarProxy } from '../avatar/AvatarFactory.js';
import {
  canonicalSkeleton,
  computeComplexity,
  complexityTier,
  type AvatarAppearance,
  type BodyTemplate,
} from '@shared/avatar.js';
import { DISPLAY_NAME_COOLDOWN_MICROS } from '@shared/types/identity.js';

const PREVIEW_DISTANCE = 2.4;

export class AtlasPassportEditorModule implements IModule {
  readonly name = 'AtlasPassportEditor';

  private scene!: THREE.Scene;
  private panel!: HTMLElement;
  private skinIn!: HTMLInputElement;
  private primaryIn!: HTMLInputElement;
  private secondaryIn!: HTMLInputElement;
  private templateBtns: HTMLButtonElement[] = [];
  private complexityEl!: HTMLElement;
  private saveBtn!: HTMLButtonElement;
  private saveNote!: HTMLElement;
  private nameIn!: HTMLInputElement;
  private renameBtn!: HTMLButtonElement;
  private renameNote!: HTMLElement;

  private open = false;
  private body: BodyTemplate = 'humanoid';
  private preview: AvatarProxy | null = null;
  private canvas!: HTMLCanvasElement;

  init(app: App): void {
    this.scene = app.world.scene;
    this.canvas = app.renderer.native.domElement as HTMLCanvasElement;

    this.panel = document.getElementById('passport')!;
    this.skinIn = document.getElementById('pp-skin') as HTMLInputElement;
    this.primaryIn = document.getElementById('pp-primary') as HTMLInputElement;
    this.secondaryIn = document.getElementById('pp-secondary') as HTMLInputElement;
    this.complexityEl = document.getElementById('pp-complexity')!;
    this.saveBtn = document.getElementById('pp-save') as HTMLButtonElement;
    this.saveNote = document.getElementById('pp-save-note')!;
    this.nameIn = document.getElementById('pp-displayname') as HTMLInputElement;
    this.renameBtn = document.getElementById('pp-rename') as HTMLButtonElement;
    this.renameNote = document.getElementById('pp-rename-note')!;

    for (const btn of Array.from(document.querySelectorAll<HTMLButtonElement>('#pp-templates button'))) {
      this.templateBtns.push(btn);
      btn.addEventListener('click', () => {
        this.body = (btn.dataset.body as BodyTemplate) ?? 'humanoid';
        this.refreshTemplateButtons();
        this.refreshPreview();
      });
    }

    for (const input of [this.skinIn, this.primaryIn, this.secondaryIn]) {
      input.addEventListener('input', () => this.refreshPreview());
    }

    this.saveBtn.addEventListener('click', () => this.save());
    this.renameBtn.addEventListener('click', () => this.rename());
    (document.getElementById('pp-close') as HTMLButtonElement).addEventListener('click', () => this.close());

    // Tab switching (Avatar / Inventory).
    for (const btn of Array.from(document.querySelectorAll<HTMLButtonElement>('#pp-tabs button'))) {
      btn.addEventListener('click', () => this.switchTab(btn.dataset.tab ?? 'avatar'));
    }

    // P toggles; Escape closes. Ignore P while typing into a field.
    window.addEventListener('keydown', (e) => {
      const typing =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement;
      if (e.key === 'Escape' && this.open) {
        this.close();
      } else if ((e.key === 'p' || e.key === 'P') && !typing && atlas.hasAccount) {
        e.preventDefault();
        this.toggle();
      }
    });

    // Sync the panel + authoritative complexity when the avatar row resolves.
    bus.on('atlas:avatar-updated', ({ isLocal }) => {
      if (isLocal && this.open) this.syncFromState();
    });
    bus.on('atlas:displayname-updated', () => {
      if (this.open) this.refreshRenameNote();
    });

    // Keep the live preview in front of the camera while the panel is open.
    app.world.addSystem(makeSystem('AtlasPassportPreview', Phase.UI, 10, () => this.placePreview()));
  }

  // ─── Open / close ────────────────────────────────────────────────────────────

  private toggle(): void {
    if (this.open) this.close();
    else this.openPanel();
  }

  private switchTab(tab: string): void {
    for (const btn of Array.from(document.querySelectorAll<HTMLButtonElement>('#pp-tabs button'))) {
      btn.classList.toggle('active', btn.dataset.tab === tab);
    }
    for (const body of Array.from(document.querySelectorAll<HTMLElement>('[data-tab-body]'))) {
      body.classList.toggle('active', body.dataset.tabBody === tab);
    }
    // The live avatar preview only makes sense on the Avatar tab.
    if (tab === 'avatar') this.refreshPreview();
    else this.disposePreview();
  }

  private openPanel(): void {
    if (!atlas.hasAccount) return;
    this.open = true;
    this.panel.classList.add('show');
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
    this.syncFromState();
    this.refreshPreview();
    this.refreshRenameNote();
  }

  private close(): void {
    this.open = false;
    this.panel.classList.remove('show');
    this.disposePreview();
  }

  /** Load the current authoritative avatar + name into the editor fields. */
  private syncFromState(): void {
    const a = atlas.localAvatar?.appearance;
    if (a) {
      this.body = a.bodyTemplate;
      this.skinIn.value = a.materialSlots.skin?.tint ?? '#c8a07a';
      this.primaryIn.value = a.materialSlots.primary?.tint ?? '#3b6ea5';
      this.secondaryIn.value = a.materialSlots.secondary?.tint ?? '#2a2f38';
    }
    this.nameIn.value = atlas.displayName ?? '';
    this.refreshTemplateButtons();
    this.updateComplexity(atlas.localAvatar?.complexity);
  }

  // ─── Appearance ────────────────────────────────────────────────────────────

  /** Build the appearance from the current editor fields. */
  private currentAppearance(): AvatarAppearance {
    return {
      bodyTemplate: this.body,
      skeletonId: canonicalSkeleton(this.body),
      materialSlots: {
        skin: { tint: this.skinIn.value },
        primary: { tint: this.primaryIn.value },
        secondary: { tint: this.secondaryIn.value },
      },
      attachments: {},
    };
  }

  private refreshTemplateButtons(): void {
    for (const btn of this.templateBtns) {
      btn.classList.toggle('active', btn.dataset.body === this.body);
    }
  }

  private refreshPreview(): void {
    if (!this.open) return;
    this.disposePreview();
    this.preview = buildAvatar(this.currentAppearance());
    this.scene.add(this.preview.object);
    this.placePreview();
    // Live (client-mirrored) complexity; the saved value comes from the server.
    this.updateComplexity(computeComplexity(this.currentAppearance()), true);
  }

  private placePreview(): void {
    if (!this.open || !this.preview || !atlas.localMesh) return;
    const yaw = atlas.localYaw;
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    this.preview.object.position.set(
      atlas.localX + fx * PREVIEW_DISTANCE,
      0,
      atlas.localZ + fz * PREVIEW_DISTANCE,
    );
    this.preview.object.rotation.y = yaw + Math.PI; // face the player
  }

  private disposePreview(): void {
    if (this.preview) {
      this.scene.remove(this.preview.object);
      this.preview.dispose();
      this.preview = null;
    }
  }

  private updateComplexity(value: number | undefined, live = false): void {
    if (value === undefined) {
      this.complexityEl.innerHTML = 'Avatar Complexity: <b>—</b>';
      return;
    }
    const tier = complexityTier(value);
    const label = live ? '(preview)' : '(saved)';
    this.complexityEl.innerHTML =
      `Avatar Complexity: <b>${value}</b> <span class="tier-${tier}">${tier}</span> ${label}`;
  }

  // ─── Reducers ────────────────────────────────────────────────────────────────

  private save(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const a = this.currentAppearance();
    this.saveBtn.disabled = true;
    this.saveNote.className = 'note';
    this.saveNote.textContent = 'Saving…';
    void conn.reducers
      .setAvatar({
        bodyTemplate: a.bodyTemplate,
        skeletonId: a.skeletonId,
        materialSlots: JSON.stringify(a.materialSlots),
        attachments: JSON.stringify(a.attachments),
      })
      .then(() => {
        this.saveBtn.disabled = false;
        this.saveNote.className = 'note ok';
        this.saveNote.textContent = 'Saved. Your identity updated everywhere.';
      })
      .catch((err: Error) => {
        this.saveBtn.disabled = false;
        this.saveNote.className = 'note err';
        this.saveNote.textContent = err.message || 'Save failed.';
      });
  }

  private rename(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const newName = this.nameIn.value.trim();
    if (newName.length < 1) {
      this.renameNote.className = 'note err';
      this.renameNote.textContent = 'Enter a display name.';
      return;
    }
    this.renameBtn.disabled = true;
    this.renameNote.className = 'note';
    this.renameNote.textContent = 'Changing…';
    void conn.reducers
      .changeDisplayName({ newName })
      .then(() => {
        this.renameBtn.disabled = false;
        this.renameNote.className = 'note ok';
        this.renameNote.textContent = 'Display name changed.';
      })
      .catch((err: Error) => {
        this.renameBtn.disabled = false;
        this.renameNote.className = 'note err';
        this.renameNote.textContent = err.message || 'Change failed.';
      });
  }

  private refreshRenameNote(): void {
    const last = atlas.displayNameLastChangedMicros;
    if (last === 0n) {
      this.renameNote.className = 'note';
      this.renameNote.textContent = 'You can change your display name now.';
      this.renameBtn.disabled = false;
      return;
    }
    const nowMicros = BigInt(Date.now()) * 1000n;
    const remaining = DISPLAY_NAME_COOLDOWN_MICROS - (nowMicros - last);
    if (remaining <= 0n) {
      this.renameNote.className = 'note';
      this.renameNote.textContent = 'You can change your display name now.';
      this.renameBtn.disabled = false;
    } else {
      const days = Number(remaining / 86_400_000_000n) + 1;
      this.renameNote.className = 'note';
      this.renameNote.textContent = `Next change available in ~${days} day(s).`;
      this.renameBtn.disabled = true;
    }
  }

  dispose(): void {
    this.disposePreview();
  }
}
